import * as React from 'react';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {LeadChevronRegularIcon} from 'shells/icon';
import {DripsRegularIcon} from 'shells/icon/svg/drips-regular-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {TagRegularDiagonalIcon} from 'shells/icon/svg/tag-regular-diagonal-icon';
import {Divider} from 'shells/layout/divider';
import {Stack} from 'shells/layout/stack/stack';
import type {ListOption} from 'shells/select-option-components/option';
import type {ListOptionWithCount} from 'shells/select-option-components/option-with-count';
import type {SelectWithIconOption} from 'shells/select-picker/select-with-icon/select-with-icon';
import {Sidebar} from 'shells/sidebar/sidebar';
import {MARKETING_AUTOMATIONS_TOUR_ACTION_CONFIGURATION_ID} from 'shells/tours/tours';
import {Body} from 'shells/typography/body';

import type {ActionType} from 'nutshell-graphql-types';
import type {
    GetMoneyPage_maUrl_automation_actions as AutomationAction,
    ActionInput,
} from 'nutshell-graphql-types';
import {SelectWithIcon} from 'shells/select-picker/../select-picker/select-with-icon/select-with-icon';

import {CreateLeadFormFields} from '../../core/form/fields/create-lead-form-fields';
import type {Option} from '../../core/select/graphql-select-tags';
import {SelectAudience} from '../../forms/form-page/editor/mapping/additional-rules/select-audience';

import {
    getInitialTagsFromAction,
    getInitialAudienceIdFromAction,
    getInitialRemoveAudienceIdFromAction,
    getInitialDripSequenceIdFromAction,
    getInitialPipelineIdFromAction,
    getInitialAssigneeIdFromAction,
    getInitialLeadTagsFromAction,
    getAssigneeIdOrRule,
} from './helpers';
import {SelectEmDripSequence} from './selects/select-em-drip-sequence';
import {TagSelectList} from './selects/tag-select-list';
import type {SelectedTag} from './selects/tag-select-list';

type Props = {
    existingAutomationAction: AutomationAction | null | undefined;
    onClose: () => void;
    handleSave: (
        automationActionToSave: ActionInput,
        newType: ActionType | null | undefined
    ) => void;
    onDelete: (automationActionToDelete: AutomationAction) => Promise<any> | null | undefined;
    isSaving: boolean;
    saveFailedErrorMessage: string | null | undefined;
    configuredTypesSet: Set<ActionType>;
};

export function CreateEditAutomationSidebarForm(props: Props) {
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [automationAction, setAutomationAction] = React.useState<ActionInput | null | undefined>(
        null
    );
    const [automationType, setAutomationType] = React.useState<ActionType | null | undefined>(
        props.existingAutomationAction ? props.existingAutomationAction.type : null
    );
    const [selectedTags, setSelectedTags] = React.useState<SelectedTag[]>(
        getInitialTagsFromAction(props.existingAutomationAction)
    );
    const [selectedAudienceId, setSelectedAudienceId] = React.useState<string | null | undefined>(
        getInitialAudienceIdFromAction(props.existingAutomationAction)
    );
    const [selectedRemoveAudienceId, setSelectedRemoveAudienceId] = React.useState<
        string | null | undefined
    >(getInitialRemoveAudienceIdFromAction(props.existingAutomationAction));
    const [selectedDripSequenceId, setSelectedDripSequenceId] = React.useState<
        string | null | undefined
    >(getInitialDripSequenceIdFromAction(props.existingAutomationAction));
    const [selectedPipelineId, setSelectedPipelineId] = React.useState<string | null | undefined>(
        getInitialPipelineIdFromAction(props.existingAutomationAction)
    );
    const [selectedAssigneeId, setSelectedAssigneeId] = React.useState<string | null | undefined>(
        getInitialAssigneeIdFromAction(props.existingAutomationAction)
    );
    const [selectedLeadTags, setSelectedLeadTags] = React.useState<Option[]>(
        getInitialLeadTagsFromAction(props.existingAutomationAction)
    );

    React.useEffect(() => {
        if (!automationType) {
            setAutomationAction(null);

            return;
        }

        if (automationType === 'ADD_TAGS' && selectedTags.length > 0) {
            setAutomationAction({
                config: {
                    addTagIds: selectedTags.map((tag) => tag.id),
                },
            });
        } else if (automationType === 'ADD_AUDIENCES' && selectedAudienceId) {
            setAutomationAction({
                config: {
                    addAudienceIds: [selectedAudienceId],
                },
            });
        } else if (automationType === 'REMOVE_AUDIENCES' && selectedRemoveAudienceId) {
            setAutomationAction({
                config: {
                    removeAudienceIds: [selectedRemoveAudienceId],
                },
            });
        } else if (automationType === 'ADD_TO_DRIP_SEQUENCE' && selectedDripSequenceId) {
            setAutomationAction({
                config: {
                    dripSequenceId: selectedDripSequenceId,
                },
            });
        } else if (automationType === 'CREATE_LEAD' && selectedPipelineId && selectedAssigneeId) {
            setAutomationAction({
                config: {
                    createLead: {
                        stagesetId: selectedPipelineId,
                        assigneeOrRule: getAssigneeIdOrRule(selectedAssigneeId),
                        tagIds: selectedLeadTags.map((tag) => tag.id),
                    },
                },
            });
        } else {
            setAutomationAction(null);
        }
    }, [
        automationType,
        selectedTags,
        selectedAudienceId,
        selectedRemoveAudienceId,
        selectedDripSequenceId,
        selectedPipelineId,
        selectedAssigneeId,
        selectedLeadTags,
    ]);

    const ACTION_OPTIONS: Partial<Record<ActionType, SelectWithIconOption>> = {
        ADD_TAGS: {
            value: 'ADD_TAGS',
            label: 'Add a tag to a person',
            icon: TagRegularDiagonalIcon,
            isDisabled:
                props.configuredTypesSet.has('ADD_TAGS' as ActionType) &&
                props.existingAutomationAction?.type !== 'ADD_TAGS',
        },
        ADD_AUDIENCES: {
            value: 'ADD_AUDIENCES',
            label: 'Add to an audience',
            icon: PeopleRegularIcon,
            isDisabled:
                props.configuredTypesSet.has('ADD_AUDIENCES' as ActionType) &&
                props.existingAutomationAction?.type !== 'ADD_AUDIENCES',
        },
        REMOVE_AUDIENCES: {
            value: 'REMOVE_AUDIENCES',
            label: 'Remove from an audience',
            icon: PeopleRegularIcon,
            isDisabled:
                props.configuredTypesSet.has('REMOVE_AUDIENCES' as ActionType) &&
                props.existingAutomationAction?.type !== 'REMOVE_AUDIENCES',
        },
        ADD_TO_DRIP_SEQUENCE: {
            value: 'ADD_TO_DRIP_SEQUENCE',
            label: 'Add to a drip sequence',
            icon: DripsRegularIcon,
            isDisabled:
                props.configuredTypesSet.has('ADD_TO_DRIP_SEQUENCE' as ActionType) &&
                props.existingAutomationAction?.type !== 'ADD_TO_DRIP_SEQUENCE',
        },
        CREATE_LEAD: {
            value: 'CREATE_LEAD',
            label: 'Create a new lead',
            icon: LeadChevronRegularIcon,
            isDisabled:
                props.configuredTypesSet.has('CREATE_LEAD' as ActionType) &&
                props.existingAutomationAction?.type !== 'CREATE_LEAD',
        },
    };

    const ACTION_TYPE_SELECT_COMPONENT_MAP: Partial<Record<ActionType, React.ReactNode>> = {
        ADD_TAGS: <TagSelectList selectedTags={selectedTags} onChange={setSelectedTags} />,
        ADD_AUDIENCES: (
            <SelectAudience
                onChange={(option: ListOptionWithCount) => setSelectedAudienceId(option.value)}
                selectedId={selectedAudienceId}
            />
        ),
        REMOVE_AUDIENCES: (
            <SelectAudience
                onChange={(option: ListOptionWithCount) =>
                    setSelectedRemoveAudienceId(option.value)
                }
                selectedId={selectedRemoveAudienceId}
            />
        ),
        ADD_TO_DRIP_SEQUENCE: (
            <SelectEmDripSequence
                onChange={(option: ListOption) => setSelectedDripSequenceId(option.value)}
                selectedId={selectedDripSequenceId}
            />
        ),
        CREATE_LEAD: (
            <CreateLeadFormFields
                pipelineId={selectedPipelineId}
                assigneeId={selectedAssigneeId}
                tags={selectedLeadTags}
                onPipelineChange={setSelectedPipelineId}
                onAssigneeChange={setSelectedAssigneeId}
                onTagsChange={setSelectedLeadTags}
            />
        ),
    };

    const ACTION_TYPE_LABEL_MAP: Partial<Record<ActionType, string>> = {
        ADD_TAGS: 'Which tag?',
        ADD_AUDIENCES: 'Which audience?',
        REMOVE_AUDIENCES: 'Which audience?',
        ADD_TO_DRIP_SEQUENCE: 'Which drip sequence?',
    };

    const ActionTypeSelectComponent = automationType
        ? ACTION_TYPE_SELECT_COMPONENT_MAP[automationType]
        : undefined;

    return (
        <Sidebar
            isOpen={true}
            // @ts-expect-error
            headerTitle={props.onDelete ? 'Edit action' : 'Add an action'}
            preventBodyScroll={false}
            canClickOutsideToClose={false}
            canEscapeToClose={true}
            onClose={props.onClose}
            onSave={() => {
                if (!automationAction) {
                    return;
                }
                props.handleSave(automationAction, automationType);
            }}
            isSaving={props.isSaving}
            submitDisabled={props.isSaving || !automationAction}
            customPositionAttributes={{
                display: 'flex',
                flexDirection: 'column',
                right: 0,
                top: 0,
            }}
        >
            {props.saveFailedErrorMessage ? (
                <FlashBanner type='error' leftAlignContent={true}>
                    {props.saveFailedErrorMessage}
                </FlashBanner>
            ) : undefined}
            <div
                className='full-height flex flex-dir-col overflow-auto'
                id={MARKETING_AUTOMATIONS_TOUR_ACTION_CONFIGURATION_ID}
            >
                <div className='flex justify-center pad-16'>
                    <Body size='small' color='light' isFullWidth={true}>
                        Configure automatic actions when this webpage is viewed by a known visitor
                    </Body>
                </div>
                <div className='px-16'>
                    <Divider variant='line--horizontal' />
                </div>
                <div className='flex flex-dir-col justify-sb flex-grow'>
                    <div className='flex flex-dir-col px-16 pt-16 gap-24'>
                        <Stack spacing={6}>
                            <Body>What should happen when a person visits this page?</Body>
                            <SelectWithIcon
                                options={Object.values(ACTION_OPTIONS)}
                                value={automationType}
                                // @ts-expect-error
                                onChange={(value) => {
                                    setAutomationType(value);
                                }}
                                placeholder='Select an action'
                            />
                        </Stack>
                        {automationType && ActionTypeSelectComponent ? (
                            <Stack spacing={6}>
                                <Body>{ACTION_TYPE_LABEL_MAP[automationType]}</Body>
                                {ActionTypeSelectComponent}
                            </Stack>
                        ) : undefined}
                    </div>
                    {/* @ts-expect-error */}
                    {props.onDelete ? (
                        <div className='full-width flex justify-end pad-16 border-box'>
                            <Button
                                onClick={() => {
                                    setIsDeleting(true);
                                }}
                                variant='danger'
                                size='big'
                                isFullWidth={true}
                            >
                                {isDeleting ? 'Removing…' : 'Delete'}
                            </Button>
                        </div>
                    ) : undefined}
                </div>
                {isDeleting && props.onDelete ? (
                    <ConfirmationDialog
                        title='Are you sure you want to delete this automation?'
                        message='This action cannot be undone.'
                        confirmText='Delete'
                        cancelText='Cancel'
                        onCancel={() => {
                            setIsDeleting(false);
                        }}
                        onConfirm={() => {
                            setIsDeleting(false);
                            if (!props.existingAutomationAction || !props.onDelete) {
                                return;
                            }
                            props.onDelete(props.existingAutomationAction);
                        }}
                        isConfirmDestructive={true}
                    />
                ) : undefined}
            </div>
        </Sidebar>
    );
}
